import type { Bi } from './sectors'

/**
 * Privacy Policy + Cookie Usage Policy — text supplied by BCITI (English only).
 * Inline links use the `[label](href)` syntax, rendered by pages/PrivacyPolicy.tsx.
 */

export type PolicyBlock =
  | { type: 'p'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'list'; items: string[] }

export type PolicySection = { id: string; title: string; blocks: PolicyBlock[] }

export type PolicyDocument = { id: string; title: string; intro?: PolicyBlock[]; sections: PolicySection[] }

export const privacyPage = {
  eyebrow: { fr: 'Mentions légales', en: 'Legal' } as Bi,
  effectiveDate: { fr: 'Date d\'entrée en vigueur : août 2024', en: 'Effective date: August, 2024' } as Bi,
  tocLabel: { fr: 'Sur cette page', en: 'On this page' } as Bi,
  contactEmail: 'privatelife@bciti.com',
}

export const privacyPolicy: PolicyDocument = {
  id: 'privacy',
  title: 'Privacy Policy',
  sections: [
    {
      id: 'introduction',
      title: '1. Introduction and Scope',
      blocks: [
        {
          type: 'p',
          text: 'This Privacy Policy (the "Policy") outlines the practices of Solutions B-CITI Inc. (hereinafter referred to as "B-CITI", "we", "our", or "us") regarding the processing of your personal information, as defined in the [Definition of "personal information"](#definition) section below. Specifically, the purpose of this Policy is to explain how and for what purposes B-CITI collects, uses, shares or otherwise processes your personal information and how we safeguard the confidentiality of your personal information when you use our website ("Website"), access your B-CITI account (“B-CITI Account”) through our Website, mobile BCITI+ application ("App"), or the website of the city or region in which your reside, as well as when you communicate with us in person, by phone or by email, including when you apply for an opportunity with B-CITI.',
        },
        {
          type: 'p',
          text: 'Please take the time to familiarize yourself with this Policy, as it describes your privacy choices and how you can contact us if you have questions or would like to exercise your rights under applicable privacy laws. By providing us with personal information, you agree that your personal information will be processed in accordance with this Policy.',
        },
      ],
    },
    {
      id: 'definition',
      title: '2. Definition of "personal Information"',
      blocks: [
        {
          type: 'p',
          text: 'In this Policy, “personal information” generally means any information that can be used, either alone or in combination with other information, to identify an individual. This includes information such as your name, mailing address, e-mail address, telephone number, or any other contact details. It may also include other types of more technical information, but only when this information can identify you as an individual. Information that is anonymized and cannot be associated with an identifiable individual is not considered to be personal information, if it is, at all times, reasonably foreseeable in the circumstances that this information irreversibly no longer allows the individual to be identified directly or indirectly.',
        },
      ],
    },
    {
      id: 'collection',
      title: '3. Personal Information We Collect',
      blocks: [
        {
          type: 'p',
          text: 'The personal information we collect about you depends on the nature of your interactions with B-CITI. Below is a description of the personal information we collect about you, according to our potential interactions with you.',
        },
        { type: 'h3', text: 'A - Information we collect when you access your B-CITI Account' },
        {
          type: 'p',
          text: 'We generally collect the following information directly from you when you access your B-CITI Account through our App, Website, or the website of your city or region in which you reside:',
        },
        {
          type: 'list',
          items: [
            '**Contact information**, such as your first name, last name, email address when you create your account on our App. Note that this is the only personal information that you are obliged to provide us to use our App. All other information is optional depending on the features you use on our App.',
            '**Demographic information**, such as your address, profile picture, phone number, government-issued ID card, gender and date of birth if you request us to generate a paper and/or paper virtual citizen card via your B-CITI Account.',
            '**Location information**, such as the name of the city in which you live in to provide you with the correct information via your B-CITI Account and, with your consent, your geolocation information to provide you with services that require us to process such information (e.g., inform you of location-based incidents such as a water issue, road closure or potholes).',
            '**Information about family members**, including who is part of your family if you would like to manage or share a subscription.',
            '**Vehicle information**, such as your license plate number to provide you with services involving your vehicle such as parking payment processing.',
            '**Payment information**, such as your credit card number to process transactions you make through your B-CITI Account.',
            '**Other information**, including any information you may share with us when submitting a request through your B-CITI Account (e.g., to signal a road closure, requiring assistance, etc.).',
          ],
        },
        {
          type: 'p',
          text: 'We also collect information automatically when you use your B-CITI Account using technologies such as cookies and pixels. Cookies are small text files that are stored on your device when you use our B-CITI Account so that information can be saved between visits, such as your login credentials or language preferences. The technical information we collect when you use our B-CITI Account includes information about the device you are using, including (as applicable) information about your device (e.g., IP address and device ID) and information about how you use our B-CITI Account (e.g., the date and time of your visit and the features of our B-CITI Account that you use). Please refer to our [Cookie Usage Policy](#cookies) to learn more about how we use cookies and how you may limit the technical information we collect about you.',
        },
        { type: 'h3', text: 'B – Information we collect when you use our Website' },
        { type: 'p', text: 'We may collect the following information directly from you when you use our Website:' },
        {
          type: 'list',
          items: [
            '**Contact information**, including your email address if you subscribe to our newsletter or communicate with us via our Demo Request Form.',
            '**Professional information**, including information found in your CV and cover letter, information on your professional experiences, education history, and the contact information of your referrals if you apply for an opportunity with B-CITI through our Website. We may also receive this information from third parties if you apply for an opportunity with B-CITI through a third-party platform (e.g., LindkedIn or Indeed).',
            '**Other information**, including any information you may share with us when using our Contact Us Form.',
          ],
        },
        {
          type: 'p',
          text: 'We also collect information automatically when you use our Website using technologies such as cookies and pixels. The technical information we collect when you visit our Website includes information about the device you are using, including (as applicable) your Internet, telecom or mobile carrier and service provider, IP address, device ID, the date and time of your visit, the pages of the Website you visit, and the referring URLs you came from. Please refer our [Cookie Usage Policy](#cookies) to learn more about how we use cookies and how you may limit the technical information we collect about you.',
        },
        { type: 'h3', text: 'C – Information we collect when you use our customer service' },
        {
          type: 'p',
          text: 'When you interact with a member of our customer support team via email or phone, we may collect the following information directly from you:',
        },
        {
          type: 'list',
          items: [
            '**Contact information**, including your name, email, and city.',
            '**Other information**, including any information that you choose to provide.',
          ],
        },
      ],
    },
    {
      id: 'use',
      title: '4. How We Use Your Personal Information',
      blocks: [
        {
          type: 'p',
          text: 'We may use your personal information for general business purposes such as providing or delivering our services, managing our Website and App, preventing or reducing commercial risk, as well as for purposes relating to our information system or network security. More specifically, we may use your personal information for the following general business purposes:',
        },
        {
          type: 'list',
          items: [
            '**Providing or delivering our products and services, including our Website, App and your B-CITI Account.** We may use your personal information as applicable to set up and administer your B-CITI Account, provide you with a Digital Citizen Card, assist you in using our B-CITI Account services such as processing payment for vehicle parking and activity subscriptions, managing your subscriptions to your city’s activities, and sending you alerts and notifications about local news (e.g., via SMS, push-notifications or emails sent on behalf of local businesses to notify you about local events and on behalf of the region in which you reside to notify you about emergencies such as forest fires or road closures).',
            '**Managing our Website, App, and B-CITI Account.** We may use your personal information to manage and facilitate the use of our Website, App, and your B-CITI Account when you visit or access them, which may include using cookies and other similar technologies (see our [Cookie Usage Policy](#cookies) for more information).',
            '**Communicating with you.** If you contact us with a question or inquiry, we may use your personal information to respond to you. We may also communicate with you for business development purposes, to promote our services and offers, send you our newsletter, to facilitate your participation in promotions, and to notify you about changes to our services.',
            '**For other purposes permitted or required by law.** We may use personal information without your consent where we are legally permitted or required to do so by law, for example, to comply with a legal obligation or to protect the rights of B-CITI or to enforce or apply our terms of use and other agreements, or to detect, prevent and investigate suspicious, unauthorized, fraudulent, illegal or unlawful activity.',
          ],
        },
      ],
    },
    {
      id: 'sharing',
      title: '5. With Whom We Share Your Personal Information',
      blocks: [
        {
          type: 'p',
          text: 'To operate our App and Website and to provide you with our services, we share information with city and municipal administrators that use our services, and certain other partners and service providers to conduct our business activities and to protect our rights. For example, we share information with the following entities:',
        },
        {
          type: 'list',
          items: [
            '**Cities, municipal administrators, and other urban partners.** We may share your personal information with the cities and municipal administrators that sign up to connect with you and provide our services. We may also share your personal information as necessary to allow cities, municipal administrators, and other urban partners such as local retailers to connect with you via your B-CITI Account for the purposes of informing you of ongoing events and relevant news in your area. You can always adjust your privacy preferences by following the instructions detailed in the [How to Exercise Your Rights](#exercise-rights)',
            '**Connection partners.** We may share your personal information with local organizations offering services related to the region or city in which you reside for the purpose of connecting you with such services. For example, we may share your information with partners that allow the processing of your local tax payments, the management of library requests, etc.',
            '**Service Providers.** We may use service providers to provide certain services on our behalf, such as data hosting and processing, analytics, marketing, SMS communications, transaction processing, and similar services. As a result, these service providers may process or handle personal information on our behalf.',
            '**Business Transactions.** We may disclose personal information to facilitate or enable any corporate or commercial transaction involving B-CITI.',
          ],
        },
        {
          type: 'p',
          text: 'Note that within B-CITI, our staff may have access, in whole or in part, to your personal information on a need-to-know basis.',
        },
      ],
    },
    {
      id: 'children',
      title: '6. Notice for Children Under the Age of 14',
      blocks: [
        {
          type: 'p',
          text: 'We do not knowingly collect personal information from individuals under the age of 14. If you are a minor under the age of 14, you may not provide us with personal information without the express consent of a parent or legal guardian. If you are a parent or guardian and you are aware that your children have provided us with personal information, please contact us. If we discover that we have collected personal information from a person under the age of 14 without obtaining the consent of the parent or guardian, we will take necessary steps to delete this information from our systems.',
        },
      ],
    },
    {
      id: 'retention',
      title: '7. Retention of Personal Information',
      blocks: [
        {
          type: 'p',
          text: 'We will retain your personal information for as necessary to fulfill the purposes for which the information was collected. However, we may retain your personal information for a longer period when required to comply with our legal and regulatory obligations regarding information or record retention.',
        },
      ],
    },
    {
      id: 'transfers',
      title: '8. Cross-border Transfers',
      blocks: [
        {
          type: 'p',
          text: 'Some information is held in paper format at our employees’ workstations. Your personal information is also held virtually on several servers and platforms, including, but not limited to, B-CITI\'s servers, cloud service provider platforms that we use, or on servers of third parties with whom we do business. When using these platforms, it is likely that your personal information may be transferred and hosted outside of the Province of Quebec or Canada by these providers, including in the United States and Europe. Organizations based outside of Canada may not be subject to privacy laws that provide you with the same rights and protections as in the Province of Quebec or Canada. As a result, when your personal information is used or stored in a jurisdiction other than where you are residing, it may be subject to the law of this foreign jurisdiction, including any law permitting or requiring disclosure of the information to the government, government agencies, courts and law enforcement in that jurisdiction. And',
        },
      ],
    },
    {
      id: 'rights',
      title: '9. Your Rights',
      blocks: [
        {
          type: 'p',
          text: 'Under Canadian privacy laws, you have the following rights with respect to your personal information, subject to limited exceptions set out in such laws:',
        },
        {
          type: 'list',
          items: [
            '**Right to information.** You have the right to ask us about our processing of your personal information, including about the categories of persons who have access to your personal information within B-CITI and our applicable retention period.',
            '**Right to access.** You can ask us if we hold any personal information about you, and if so, you can request access to that personal information.',
            '**Right to correction.** You have the right to ask us to rectify any personal information that you demonstrate to be incorrect or outdated and to complete any information that you demonstrate to be incomplete.',
            '**Right to withdraw consent.** You may withdraw your consent to our processing of your personal information to the extent permitted by law and our contractual obligations. However, you understand that in such a case, you may no longer be able to benefit from our products and services.',
            '**Right to data portability.** Depending on the laws applicable in the region in which you reside, you may have the right to ask us to communicate to you the personal information you have provided us in a structured, commonly used technological format, as well as to a third party upon your request.',
            '**Automated decision-making.** B-CITI shall inform you of any use of your personal information for the purpose of rendering a decision based exclusively on an automated processing of such information as required by applicable laws. In such a scenario and upon request, you may have the right to obtain details related to the processing of your personal information to render the decision, including to obtain the reasons and the principal factors and parameters that led to the decision.',
          ],
        },
      ],
    },
    {
      id: 'exercise-rights',
      title: '10. How to Exercise Your Rights',
      blocks: [
        {
          type: 'p',
          text: 'To exercise any of your privacy rights, please contact us using the information provided in the [Contact Us](#contact) section below. You can also access and correct some of your personal information directly by signing into your B-CITI Account and accessing our traceability tool. This tool may indeed notably provide you with information about how you may manage your consent preferences, the categories of individuals that have accessed your information, and how your personal information has been processed in the context of automated decision-making, if applicable.',
        },
      ],
    },
    {
      id: 'third-party',
      title: '11. Third Part Sites or Services',
      blocks: [
        {
          type: 'p',
          text: 'This Policy does not apply to third-party websites, pages, or applications that can be accessed through our products and services, including, but not limited to, through our Website and App, and we are not responsible for such third-party services. If you follow such links, these third-party sites or services will likely have their own policies on the protection of personal information that you should review before submitting your personal information.',
        },
      ],
    },
    {
      id: 'security',
      title: '12. Security Measures',
      blocks: [
        {
          type: 'p',
          text: 'We protect and safeguard your personal information using physical, electronic and procedural measures appropriate to the volume and sensitivity of the information, the purposes for which it is used, and the format in which it is stored, including safeguards to protect your information against loss and theft, as well as unauthorized access, disclosure, copying, use, or modification. Specifically, we implement the following measures to protect your personal information, including:',
        },
        {
          type: 'list',
          items: [
            'Internal policies and procedures that define the roles and responsibilities of our employees, representatives, and service providers throughout the life cycle of an item of information and limit their access to that information on a “need-to-know” basis;',
            'Physical, electronic and procedural safeguards that comply with relevant standards to protect personal information;',
            'If the information is collected or stored electronically, technical safeguards such as encryption, firewalls, passwords, anti-virus software and similar measures;',
            'A designated privacy officer to monitor B-CITI’s compliance with applicable privacy laws;',
            'Employee training in privacy and information security;',
            'Procedures for receiving, investigating, and responding to complaints or inquiries about B-CITI’s information handling practices; and',
            'Contractual protections to ensure that service providers with whom we share personal information maintain adequate protections and security standards.',
          ],
        },
        {
          type: 'p',
          text: 'Note that despite these measures, given the inherent risks associated with the use of computer systems, we cannot guarantee or warrant the security and confidentiality of the personal information you transmit or provide to us. You do so at your own risk.',
        },
        {
          type: 'p',
          text: 'If you have reason to believe that personal information has been compromised, please contact us using the information provided in the [Contact Us](#contact) section.',
        },
      ],
    },
    {
      id: 'updates',
      title: '13. Updates to this Policy',
      blocks: [
        {
          type: 'p',
          text: 'This Policy is current as of the Effective Data set forth above and may be amended from time to time at our sole discretion. We recommend that you periodically review this Policy as it may be updated or revised from time to time. If we make material changes, we will take reasonable steps to notify you of those changes, including by posting the revised Policy on our Website with an adequate notice.',
        },
      ],
    },
    {
      id: 'contact',
      title: '14. Contact Us',
      blocks: [
        {
          type: 'p',
          text: 'If you have any questions regarding our processing of your personal information or about this Policy, please contact our Privacy Officer in writing at [privatelife@bciti.com](mailto:privatelife@bciti.com).',
        },
      ],
    },
  ],
}

export const cookiePolicy: PolicyDocument = {
  id: 'cookies',
  title: 'Cookie Usage Policy',
  intro: [
    {
      type: 'p',
      text: 'This policy regarding the use of analytics tools and cookies ("Cookies Usage Policy") describes how and why Solutions B-CITI Inc. (hereinafter referred to as "B-CITI", "we", "our", or "us") uses cookies, web beacons, and other identification, profiling, and location technologies on our Website, App, and in our promotional commercial electronic communications (e.g., our newsletter).',
    },
    {
      type: 'p',
      text: 'Our Cookie Usage Policy complements our [Privacy Policy](#privacy) and should therefore be read in conjunction with it.',
    },
  ],
  sections: [
    {
      id: 'cookies-definition',
      title: '1. What is a Cookie, and What are Web Beacons?',
      blocks: [
        {
          type: 'p',
          text: 'Cookies are small text files that are downloaded to your device when you visit a website, use a mobile application, or open an email. Cookies allow a website to recognize a device and store information (e.g., your preferences, your usage of the Website) and retrieve this information each time you access the Website.',
        },
        {
          type: 'p',
          text: 'There are two types of cookies: (i) first-party and (ii) third-party. Essentially, both types are technically identical and perform the same functions; the main difference lies in how they are created and how they are subsequently used.',
        },
        {
          type: 'list',
          items: [
            '**First-Party Cookies.** First-party cookies are created by the Website you are visiting. A Website can only access the cookies it has placed on your device itself, so, for example, cookies placed during your visit to a search engine are not accessible by our Website.',
            '**Third-Party Cookies.** Third-party cookies are created by a different website than the one you are visiting and are primarily used for profiling and behavioral advertising purposes. They also allow Website owners to provide certain services. This type of cookie can be placed through what is called a web beacon (more information on this below). Since a website can only access the cookies it has placed itself, we cannot access third-party cookies placed on our Website.',
          ],
        },
        {
          type: 'p',
          text: 'A web beacon, often referred to as an invisible pixel, is a transparent image, typically 1 pixel by 1 pixel in size, that is placed on a website or in an email to assess how a user interacts with specific content. Web beacons operate by sending information along with the request to the third-party web server when the web beacon is requested to be displayed.',
        },
      ],
    },
    {
      id: 'cookies-control',
      title: '2. What Can You Do To Control Cookies Placed on Your Device?',
      blocks: [
        {
          type: 'p',
          text: 'Most browsers allow you to configure how cookies are accepted by your browser. Please keep in mind that changing your cookie settings may prevent some Websites from functioning correctly. Refer to your browser developer\'s documentation to learn how to configure cookie settings.',
        },
        {
          type: 'p',
          text: 'You can also use your browser\'s private or incognito mode, which will automatically remove all cookies placed during your use of private mode when all private mode tabs are closed. Check your browser\'s help section to learn more about how private mode works.',
        },
        {
          type: 'p',
          text: 'Some recent browsers have built-in features to block or control content that enables profiling. We encourage you to check your browser\'s help section to see if such a feature is offered.',
        },
        {
          type: 'p',
          text: 'Certain browsers may have third-party-created extensions that can be used to manage and delete cookies. If you decide to use extensions, please pay close attention to the extensions you install and the permissions they require.',
        },
        {
          type: 'p',
          text: 'You can also activate or de-activate certain cookies and similar technologies that allow us to perform profiling and display ads on third-party sites.',
        },
      ],
    },
    {
      id: 'cookies-use',
      title: '3. How Do We Use Cookies on Our Website?',
      blocks: [
        { type: 'p', text: 'To the extent permitted by law, we use cookies and web beacons for the following general purposes:' },
        {
          type: 'list',
          items: [
            'Analyzing traffic on our Website in general.',
            'Adjusting Website settings based on your device.',
            'Assisting us in understanding how people use our services and interact with our promotional material, so we can improve them.',
            'Analyzing your interaction with other pages and Websites.',
            'Assisting us in personalizing our approach, responses, and services by remembering your preferences.',
          ],
        },
        { type: 'p', text: 'Specifically, we use cookies and web beacons for the three following purposes:' },
        {
          type: 'list',
          items: [
            '**Traffic Analysis.** We use cookies to understand how you interact with our Website. Our partners may also use cookies to analyze your use of our Website and provide us with information about your usage.',
            '**Marketing.** Our partners Meta and LinkedIn may use cookies to determine if our advertisements have been displayed to you, and if so, whether you have interacted with them to the extent permitted by applicable law. In such cases, our partners share data and statistics with us regarding the performance of our advertisements.',
            '**Direct communications:** We use web beacons in our promotional emails to gather statistics on email openings and interaction with the content therein, with the purpose of analyzing the performance of our promotional campaigns to the extent permitted by applicable law. We use your interaction with our promotional emails as an indicator of your interest in our products and services. We may adjust how we contact you based on your interaction with our promotional emails.',
          ],
        },
      ],
    },
    {
      id: 'cookies-analytics',
      title: '4. Analytics Tools to Measure Interaction with Our Website and Promotional Material',
      blocks: [
        { type: 'h3', text: 'A- What you need to know about analytics' },
        {
          type: 'p',
          text: 'Analytics tools are used to collect, analyze, and measure web traffic and user visits on our Website for the purpose of understanding and optimizing its usage. Analytics tools operate by gathering information about a visitor\'s interactions with one or more web pages. This information may include, for example, the visitor\'s IP address.',
        },
        { type: 'h3', text: 'B- What analytics tools do we use on our Websites' },
        {
          type: 'p',
          text: 'We use our service provider HubSpot to gather information about how visitors use our Website. The information is collected in an anonymous form and includes, among other things:',
        },
        {
          type: 'list',
          items: [
            'The country and city from which you access our Website.',
            'The number of visitors to our Website and the pages visited.',
          ],
        },
        {
          type: 'p',
          text: 'For more information on how HubSpot processes any personal information it may hold about you as a result of the services it provides to us, please refer to HubSpot\'s privacy policy available here [https://legal.hubspot.com/privacy-policy](https://legal.hubspot.com/privacy-policy)',
        },
        { type: 'h3', text: 'C- How will we use the information we have gathered from analytics tools?' },
        { type: 'p', text: 'We rely on analytics tools to obtain information about you for these main purposes:' },
        {
          type: 'list',
          items: [
            'Improving our Website based on visitor interactions.',
            'Evaluating the effectiveness of our promotional campaigns.',
            'Gaining a better understanding of the journey our visitors take from the initial campaign to becoming customers.',
            'Creating marketing profiles and qualifying potential customers to the extent permitted by applicable law.',
            'Conducting targeted advertising on search engines and social media to the extent permitted by applicable law.',
          ],
        },
        { type: 'h3', text: 'D- What can you do to prevent Websites from collecting analytic information?' },
        {
          type: 'p',
          text: 'You can take various measures to prevent a website from collecting details about your visits to our Website. You can:',
        },
        {
          type: 'list',
          items: [
            'Manage the cookies accepted by your browser (see the [What Can You do to Control Cookies Placed on Your Device?](#cookies-control) section above).',
            'Manage the use of cookies by using browser extensions.',
          ],
        },
      ],
    },
  ],
}
